import { fetchPutState } from '@iwoca/lapi-client/edge';
import {
  fetchPutApiIntroducerPortalIntroducerApplication,
  PutApiIntroducerPortalIntroducerApplicationRequestBody,
} from '@iwoca/lapi-client/introducer_portal';
import { useMutation } from '@tanstack/react-query';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import {
  TGetStateResponseData,
  TPutStateRequestBody,
} from 'lendingApi/lapiOverrides.types';
import { TSignupSteps } from 'types';
import { trimAllStrings } from 'utils/common/common';
import {
  useGetIntroducerApplication,
  useGetStateQuery,
} from '../../../lendingApi/lendingApiHooks';
import {
  denormaliseApplication,
  normaliseApplication,
} from '../helpers/applicationNormalisation';
import {
  denormaliseState,
  normaliseState,
} from '../helpers/stateNormalisation';
import { TSignupFormData } from '../SignupForm/SignupForm.types';
import { useRouterApplicationId } from './useRouterApplicationId';

export function useSignupData() {
  const { introducerId, splitTests } = useIntroducerContext();
  const applicationId = useRouterApplicationId();
  const {
    application,
    refetchApplication,
    isLoading: isLoadingApplication,
  } = useGetIntroducerApplication();

  const stateKey = application?.customer?.state_key;
  const { state, refetchState } = useGetStateQuery();
  const isReintroduction = Boolean(
    state?.ui?.reintroduction &&
      splitTests?.sameIntroducerAccountReintroductions,
  );

  const { mutateAsync: mutateState } = useMutation({
    mutationFn: async (state: TPutStateRequestBody) => {
      await fetchPutState({ stateKey: stateKey as string, body: state });
      return await refetchState();
    },
  });

  const {
    mutateAsync: mutateApplication,
    error: hasPutApplicationError,
    isPending: isLoadingPutApplication,
  } = useMutation({
    mutationFn: async (
      applicationBody: PutApiIntroducerPortalIntroducerApplicationRequestBody,
    ) => {
      await fetchPutApiIntroducerPortalIntroducerApplication({
        introducerId: introducerId as string,
        applicationId: applicationId as string,
        body: applicationBody,
      });
      return await refetchApplication();
    },
  });

  const updateSignupData = async ({
    data,
    isSaveAndFinishLater = false,
  }: {
    data: TSignupFormData;
    isSaveAndFinishLater?: boolean;
  }) => {
    if (!stateKey || !applicationId || !state) {
      throw new Error('Error updating signup data');
    }

    data = trimAllStrings(data);

    const newState = denormaliseState({
      prevState: state,
      formData: data,
      isReintroduction,
    });
    const newApplication = denormaliseApplication({
      formData: data,
      isReintroduction,
      isSaveAndFinishLater,
    });
    const stateResponse = await mutateState({ data: newState });
    await mutateApplication({ data: newApplication });

    return { updatedState: stateResponse?.data?.data as TGetStateResponseData };
  };

  const updateLoanPurposeDetails = async (detailedPurpose: string) => {
    if (!application) {
      return;
    }

    const previousProduct = application.requested_products[0];

    await mutateApplication({
      data: {
        requested_products: [
          {
            amount: previousProduct.amount,
            purpose: previousProduct.purpose,
            detailed_purpose: detailedPurpose,
            duration: previousProduct.duration,
            product_type: previousProduct.product_type,
          },
        ],
      },
    });
  };

  const updateLatestSignupStep = async (
    step: TSignupSteps,
    updatedState: TGetStateResponseData,
  ) => {
    await mutateState({
      data: {
        ...updatedState,
        ui: { ...updatedState.ui, tip_latest_signup_step: step },
      },
    });
  };

  return {
    stateKey: stateKey as string | undefined,
    state,
    stateData: normaliseState(state),
    applicationData: normaliseApplication(application),
    updateLoanPurposeDetails: updateLoanPurposeDetails,
    updateSignupData,
    updateLatestSignupStep,
    isLoadingApplication,
    hasPutApplicationError,
    isLoadingPutApplication,
    isReintroduction,
  };
}
